import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../../../shared/types/Customer';
export function CustomerDetail() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+66 12 345 6789',
    address: '123 Business Street',
    city: 'Kuala Lumpur',
    country: 'Malaysia',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const history = [{
    id: 'h1',
    action: 'Invoice INV-2024-001 paid',
    date: '10 Jan 2024 at 14:32'
  }, {
    id: 'h2',
    action: 'Invoice INV-2024-015 sent',
    date: '15 Jan 2024 at 09:15'
  }, {
    id: 'h3',
    action: 'Payment method Visa •••• 4242 added',
    date: '15 Jun 2023 at 11:00'
  }, {
    id: 'h4',
    action: `${customer.name} was created`,
    date: '15 Jun 2023 at 10:30'
  }];
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      draft: 'bg-gray-100 text-gray-800 border border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border border-blue-100',
      paid: 'bg-green-50 text-green-700 border border-green-100',
      overdue: 'bg-red-50 text-red-700 border border-red-100',
      completed: 'bg-green-50 text-green-700 border border-green-100',
      pending: 'bg-yellow-50 text-yellow-700 border border-yellow-100',
      failed: 'bg-red-50 text-red-700 border border-red-100',
      refunded: 'bg-gray-100 text-gray-800 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-4285">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-4286">
      {/* Header */}
      <div className="flex items-center" data-id="element-4287">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-4288">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-4289" />
          {t('pages.CustomerDetail.all_customers')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4290">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4291">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4292">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-4293">
              <div className="flex justify-between items-start" data-id="element-4294">
                <div className="flex items-start gap-4" data-id="element-4295">
                  <div className="w-14 h-14 bg-[#FFF4B8] rounded-full flex items-center justify-center text-[#B38A00] font-semibold text-xl flex-shrink-0" data-id="element-4296">
                    {customer.name.charAt(0)}
                  </div>
                  <div data-id="element-4297">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-4298">
                      {customer.name}
                    </h1>
                    <div className="flex items-center text-sm" data-id="element-4299">
                      <span className="text-gray-500 mr-2" data-id="element-4300">
                        {t('pages.CustomerDetail.status')}
                      </span>
                      {getStatusBadge(customer.status)}
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4301">
                  {t('pages.CustomerDetail.edit_customer')}
                </button>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-4302">
              <div className="flex justify-between items-center mb-6" data-id="element-4303">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-4304">
                  {t('pages.CustomerDetail.details')}
                </h3>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="text-sm font-medium text-[#B38A00] hover:text-[#8C6C00]" data-id="element-4305">
                  {t('pages.CustomerDetail.edit')}
                </button>
              </div>
              <div className="space-y-4" data-id="element-4306">
                <div className="grid grid-cols-3 gap-4" data-id="element-4307">
                  <div className="text-sm text-gray-500" data-id="element-4308">
                    {t('pages.CustomerDetail.email')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-4309">
                    {customer.email}
                  </div>
                </div>
                {customer.phone && <div className="grid grid-cols-3 gap-4" data-id="element-4310">
                    <div className="text-sm text-gray-500" data-id="element-4311">
                      {t('pages.CustomerDetail.phone')}
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-4312">
                      {customer.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-4313">
                  <div className="text-sm text-gray-500" data-id="element-4314">
                    {t('pages.CustomerDetail.address')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-4315">
                    {customer.address}, {customer.city}, {customer.country}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-4316">
                  <div className="text-sm text-gray-500" data-id="element-4317">
                    {t('pages.CustomerDetail.customer_since')}
                  </div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-4318">
                    {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-4319">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-4320">
                {t('pages.CustomerDetail.summary')}
              </h3>
              <div className="grid grid-cols-3 gap-6" data-id="element-4321">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-4322">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-4323">
                    {t('pages.CustomerDetail.total_spent')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-4324">
                    {t('pages.CustomerDetail.usd')}{' '}
                    {customer.totalSpent.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-4325">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-4326">
                    {t('pages.CustomerDetail.total_invoices')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-4327">
                    {customer.invoiceCount}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-4328">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-4329">
                    {t('pages.CustomerDetail.avg_invoice_value')}
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-4330">
                    {t('pages.CustomerDetail.usd')}{' '}
                    {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods Section */}
            <div className="p-8" data-id="element-4331">
              <div className="flex justify-between items-center mb-6" data-id="element-4332">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-4333">
                  {t('pages.CustomerDetail.payment_methods')}
                </h3>
                <button className="text-sm font-medium text-[#B38A00] hover:text-[#8C6C00] flex items-center gap-1.5" data-id="element-4334">
                  <PlusIcon size={14} aria-hidden="true" data-id="element-4335" />
                  {t('pages.CustomerDetail.add_card')}
                </button>
              </div>
              <div className="space-y-3" data-id="element-4336">
                {paymentMethods.map(method => <div key={method.id} className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-4337">
                    <div className="flex items-center justify-between" data-id="element-4338">
                      <div className="flex items-center gap-4" data-id="element-4339">
                        <div className="w-10 h-10 rounded-full bg-[#FFF4B8] flex items-center justify-center" data-id="element-4340">
                          <CreditCardIcon size={20} className="text-[#B38A00]" aria-hidden="true" data-id="element-4341" />
                        </div>
                        <div data-id="element-4342">
                          <div className="flex items-center gap-2" data-id="element-4343">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-4344">
                              {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                              •••• {method.last4}
                            </span>
                            {method.isDefault && <span className="px-2 py-0.5 bg-[#FFC70A]/10 text-[#B38A00] text-xs font-medium rounded-md border border-[#FFC70A]/20" data-id="element-4345">
                                {t('pages.CustomerDetail.default')}
                              </span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-4346">
                            {t('pages.CustomerDetail.expires')}{' '}
                            {method.expiryMonth}/{method.expiryYear} ·{' '}
                            {method.cardholderName}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-4347">
                        {!method.isDefault && <button className="px-3 py-1.5 text-xs font-medium text-[#B38A00] hover:bg-[#FFFBE6] rounded-lg transition-colors" data-id="element-4348">
                            {t('pages.CustomerDetail.set_as_default')}
                          </button>}
                        <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-4349">
                          <TrashIcon size={16} data-id="element-4350" />
                        </button>
                      </div>
                    </div>
                  </div>)}
                {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500 text-sm" data-id="element-4351">
                    {t('pages.CustomerDetail.no_payment_methods_stored')}
                  </div>}
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4352">
            <div className="flex space-x-6 px-8 border-b border-gray-100" role="tablist" aria-label="Customer details" data-id="element-4353">
              <button role="tab" aria-selected={activeTab === 'invoices'} aria-controls="customer-panel-invoices" id="customer-tab-invoices" onClick={() => setActiveTab('invoices')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invoices' ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4354">
                <FileTextIcon size={16} aria-hidden="true" data-id="element-4355" />
                {t('pages.CustomerDetail.invoices')}
              </button>
              <button role="tab" aria-selected={activeTab === 'transactions'} aria-controls="customer-panel-transactions" id="customer-tab-transactions" onClick={() => setActiveTab('transactions')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'transactions' ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-4356">
                <ActivityIcon size={16} aria-hidden="true" data-id="element-4357" />
                {t('pages.CustomerDetail.transactions')}
              </button>
            </div>

            {/* Invoices Tab */}
            {activeTab === 'invoices' && <div className="overflow-x-auto" data-id="element-4358">
                <table className="w-full text-left border-collapse" data-id="element-4359">
                  <thead data-id="element-4360">
                    <tr className="border-b border-gray-100" data-id="element-4361">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4362">
                        {t('pages.CustomerDetail.invoice')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4363">
                        {t('pages.CustomerDetail.date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4364">
                        {t('pages.CustomerDetail.due_date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4365">
                        {t('pages.CustomerDetail.amount')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-4366">
                        {t('pages.CustomerDetail.status_2')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-4367">
                    {customerInvoices.map(invoice => <tr key={invoice.id} onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-4368">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 group-hover:text-[#B38A00] transition-colors" data-id="element-4369">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-4370">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-4371">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-4372">
                          {t('pages.CustomerDetail.usd')}{' '}
                          {invoice.amount.toLocaleString('en-US')}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-4373">
                          {getStatusBadge(invoice.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && <div className="overflow-x-auto" data-id="element-4374">
                <table className="w-full text-left border-collapse" data-id="element-4375">
                  <thead data-id="element-4376">
                    <tr className="border-b border-gray-100" data-id="element-4377">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4378">
                        {t('pages.CustomerDetail.date')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4379">
                        {t('pages.CustomerDetail.description')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4380">
                        {t('pages.CustomerDetail.payment_method')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4381">
                        {t('pages.CustomerDetail.amount')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-4382">
                        {t('pages.CustomerDetail.status_2')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-4383">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 transition-colors" data-id="element-4384">
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-4385">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-4386">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={e => {
                      e.stopPropagation();
                      navigate(`/invoice-detail/${transaction.invoiceId}`);
                    }} className="ml-2 text-[#B38A00] hover:text-[#8C6C00] text-xs font-medium" data-id="element-4387">
                              {t('pages.CustomerDetail.view_invoice')}
                            </button>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-4388">
                          {transaction.paymentMethod || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm" data-id="element-4389">
                          <span className={`font-medium ${transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-4390">
                            {transaction.amount < 0 ? '-' : ''}
                            {t('pages.CustomerDetail.usd')}{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-US')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-4391">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-4392">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-4393">
            {t('pages.CustomerDetail.history')}
          </h3>
          <div className="space-y-6" data-id="element-4394">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-4395">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-4396"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-4397">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-4398" />
                </div>
                <div data-id="element-4399">
                  <p className="text-sm font-medium text-gray-900" data-id="element-4400">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-4401">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}